import { useEffect, useState } from "react";
import { BookOpenCheck, Download, ChevronDown, Lightbulb } from "lucide-react";
import { api, apiError, descargarPdf, API } from "@/lib/api";
import { Loading, PageTitle } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Seccion = ({ num, s, abierta, onToggle }) => (
  <div className="rounded-lg border border-border/60 bg-card" data-testid={`manual-seccion-${num}`}>
    <button onClick={onToggle} data-testid={`manual-toggle-${num}`}
            className="flex w-full items-center gap-4 px-6 py-5 text-left lift">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-sm font-bold text-primary">
        {num}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-base font-semibold">{s.titulo}</span>
        {!abierta && s.intro && (
          <span className="mt-1 block truncate text-sm text-muted-foreground">{s.intro}</span>
        )}
      </span>
      <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${abierta ? "rotate-180" : ""}`} />
    </button>

    {abierta && (
      <div className="border-t border-border/60 px-6 py-6 sm:px-8">
        {s.intro && <p className="text-sm text-muted-foreground">{s.intro}</p>}
        <ol className="mt-5 space-y-3">
          {s.pasos?.map((paso, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted font-mono text-xs font-bold">
                {i + 1}
              </span>
              <span className="pt-0.5">{paso}</span>
            </li>
          ))}
        </ol>
        {s.consejo && (
          <div className="mt-6 flex gap-3 rounded-md border border-accent/40 bg-accent/5 p-4 text-sm"
               data-testid={`manual-consejo-${num}`}>
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
            <span><span className="font-semibold">Recuerde:</span> {s.consejo}</span>
          </div>
        )}
        {s.captura && (
          <img src={`${API}/manual/captura/${s.captura}`} alt={s.titulo} loading="lazy"
               data-testid={`manual-captura-${num}`}
               className="mt-6 w-full rounded-md border border-border" />
        )}
      </div>
    )}
  </div>
);

export default function Manual() {
  const { user } = useAuth();
  const [rol, setRol] = useState("");
  const [data, setData] = useState(null);
  const [abierta, setAbierta] = useState(0);
  const [bajando, setBajando] = useState(false);

  useEffect(() => {
    setData(null);
    api.get("/manual", { params: rol ? { rol } : {} })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e, "No se pudo cargar el manual")));
  }, [rol]);

  const bajar = async (cual) => {
    setBajando(true);
    try {
      await descargarPdf("/manual/pdf", cual ? { rol: cual } : {},
                         cual === "todos" ? "manual-del-sistema-completo.pdf"
                                          : `manual-${cual || user?.rol}.pdf`);
      toast.success("Manual descargado en PDF");
    } catch (e) {
      toast.error(apiError(e, "No se pudo generar el PDF"));
    } finally {
      setBajando(false);
    }
  };

  return (
    <div data-testid="manual-uso">
      <PageTitle title="Manual de uso del sistema"
                 subtitle={data?.resumen || "Guía paso a paso de cada pantalla, con capturas y consejos."}>
        <div className="flex flex-wrap items-center gap-3">
          {data?.roles?.length > 0 && (
            <select value={rol || user?.rol} data-testid="manual-selector-rol"
                    onChange={(e) => { setRol(e.target.value); setAbierta(0); }}
                    className="rounded-full border border-border bg-background px-4 py-2 text-sm">
              {data.roles.map((r) => <option key={r.clave} value={r.clave}>{r.nombre}</option>)}
            </select>
          )}
          <Button className="rounded-full" disabled={bajando} data-testid="descargar-manual"
                  onClick={() => bajar(rol || user?.rol)}>
            <Download className="mr-2 h-4 w-4" /> {bajando ? "Generando…" : "Descargar PDF"}
          </Button>
          {data?.roles?.length > 0 && (
            <Button variant="outline" className="rounded-full" disabled={bajando}
                    data-testid="descargar-manual-todos" onClick={() => bajar("todos")}>
              <BookOpenCheck className="mr-2 h-4 w-4" /> Todos los roles
            </Button>
          )}
        </div>
      </PageTitle>

      {!data ? <Loading label="Cargando el manual…" /> : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary" data-testid="manual-rol">{data.rol_nombre}</Badge>
            <span className="text-sm text-muted-foreground">
              {data.secciones.length} temas · año lectivo {data.anio_lectivo}
            </span>
            <button className="text-sm text-primary underline-offset-4 hover:underline"
                    data-testid="manual-abrir-todo"
                    onClick={() => setAbierta(abierta === "todo" ? 0 : "todo")}>
              {abierta === "todo" ? "Cerrar todos los temas" : "Abrir todos los temas"}
            </button>
          </div>

          <div className="mt-6 space-y-3">
            {data.secciones.map((s, i) => (
              <Seccion key={s.titulo} num={i + 1} s={s}
                       abierta={abierta === "todo" || abierta === i + 1}
                       onToggle={() => setAbierta(abierta === i + 1 ? 0 : i + 1)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
